import React, { useState, useEffect } from 'react'
import Layout from '../components/Layout'
import api from '../services/api'
import { useModal } from '../components/Modal'

export default function ApprovePage() {
  const { showAlert, showConfirm, ModalComponents } = useModal()
  const [signedContracts, setSignedContracts] = useState([])
  const [witness1Contracts, setWitness1Contracts] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [currentUserData, setCurrentUserData] = useState(null)
  const [lastUpdated, setLastUpdated] = useState(new Date())
  const [showModal, setShowModal] = useState(false)
  const [selectedContract, setSelectedContract] = useState(null)
  const [contractDetail, setContractDetail] = useState(null)
  const [modalType, setModalType] = useState(null)
  const [confirmPassword, setConfirmPassword] = useState('')
  const canWitness = currentUserData?.role === 'hr' && currentUserData?.is_witness === true

  useEffect(() => {
    fetchData()
    const interval = setInterval(fetchData, 30000)
    return () => clearInterval(interval)
  }, [])

  const fetchData = async () => {
    try {
      setLoading(true)
      const [meRes, signedRes, w1Res] = await Promise.all([
        api.get('/users/me'),
        api.get('/contracts', { params: { status: 'signed' } }),
        api.get('/contracts', { params: { status: 'witness1_signed' } })
      ])
      if (meRes.data.success) setCurrentUserData(meRes.data.data)
      if (signedRes.data.success) setSignedContracts(signedRes.data.data)
      if (w1Res.data.success) setWitness1Contracts(w1Res.data.data)
    } catch (err) { console.error(err) }
    finally { setLoading(false); setLastUpdated(new Date()) }
  }

  const openModal = async (contract, type) => {
    if (!canWitness) return
    try {
      const res = await api.get(`/contracts/${contract.id}`)
      if (res.data.success) {
        setContractDetail(res.data.data)
        setSelectedContract(contract)
        setModalType(type)
        setConfirmPassword('')
        setShowModal(true)
      }
    } catch (err) { console.error(err) }
  }

  const handleViewPDF = async () => {
    try {
      const res = await api.get(`/contracts/${contractDetail.id}/pdf`, { responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
      window.open(url, '_blank')
    } catch (err) { showAlert('error', 'เกิดข้อผิดพลาด', 'ไม่สามารถเปิด PDF ได้') }
  }

  const handleWitness1Sign = async () => {
    if (!canWitness) { showAlert('warning', 'ไม่มีสิทธิ์ลงนาม', 'บัญชีของท่านไม่ได้รับมอบหมายเป็นพยาน'); return }
    if (!currentUserData?.signature_image) { showAlert('warning', 'ไม่พบลายเซ็น', 'กรุณาอัปโหลดลายเซ็นที่หน้าจัดการผู้ใช้ก่อน'); return }
    const ok = await showConfirm('warning', 'ยืนยันลงนาม', `ยืนยันลงนามพยานคนที่ 1 สัญญา ${selectedContract.contract_no} ?`)
    if (!ok) return
    try {
      setSaving(true)
      await api.post(`/contracts/${selectedContract.id}/witness1`, { confirm_password: confirmPassword })
      const res = await api.get(`/contracts/${selectedContract.id}`)
      if (res.data.success) setContractDetail(res.data.data)
      setModalType('signed1')
      fetchData()
    } catch (err) { showAlert('error', 'เกิดข้อผิดพลาด', err.response?.data?.message || 'เกิดข้อผิดพลาด') }
    finally { setSaving(false) }
  }

  const handleWitness2Sign = async () => {
    if (!canWitness) { showAlert('warning', 'ไม่มีสิทธิ์ลงนาม', 'บัญชีของท่านไม่ได้รับมอบหมายเป็นพยาน'); return }
    if (!currentUserData?.signature_image) { showAlert('warning', 'ไม่พบลายเซ็น', 'กรุณาอัปโหลดลายเซ็นที่หน้าจัดการผู้ใช้ก่อน'); return }
    if (selectedContract.witness1_user_id === currentUserData?.id) { showAlert('warning', 'ไม่สามารถลงนามได้', 'ท่านเป็นพยานคนที่ 1 แล้ว'); return }
    const ok = await showConfirm('warning', 'ยืนยันลงนาม', `ยืนยันลงนามพยานคนที่ 2 สัญญา ${selectedContract.contract_no} ?`)
    if (!ok) return
    try {
      setSaving(true)
      await api.post(`/contracts/${selectedContract.id}/approve`, { confirm_password: confirmPassword })
      const res = await api.get(`/contracts/${selectedContract.id}`)
      if (res.data.success) setContractDetail(res.data.data)
      setModalType('signed2')
      fetchData()
    } catch (err) { showAlert('error', 'เกิดข้อผิดพลาด', err.response?.data?.message || 'เกิดข้อผิดพลาด') }
    finally { setSaving(false) }
  }

  const sectionConfig = {
    witness1: { title: 'รอพยานคนที่ 1 ลงนาม', sub: 'พนักงานลงนามแล้ว รอพยานคนที่ 1', color: '#f59e0b', bg: '#fef9c3', btnColor: '#f59e0b', btnLabel: 'ตรวจสอบและลงนาม' },
    witness2: { title: 'รอพยานคนที่ 2 ลงนาม', sub: 'พยานคนที่ 1 ลงนามแล้ว รอพยานคนที่ 2', color: '#075dcc', bg: '#eff6ff', btnColor: '#075dcc', btnLabel: 'ตรวจสอบและลงนาม' },
  }

  const groupContractsByDivision = (contracts) => {
    const grouped = contracts.reduce((result, contract) => {
      const divisionName = contract.division_name?.trim() || 'ไม่ระบุกองงาน'
      if (!result[divisionName]) result[divisionName] = []
      result[divisionName].push(contract)
      return result
    }, {})

    return Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b, 'th'))
  }

  const ContractSection = ({ contracts, type }) => {
    const cfg = sectionConfig[type]
    const divisionGroups = groupContractsByDivision(contracts)
    return (
      <div style={{ marginBottom: '24px' }}>
        <div style={{ background: 'white', borderRadius: '14px', border: '0.5px solid #e2e8f0', overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '0.5px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: cfg.bg }}>
            <div>
              <p style={{ fontSize: '14px', fontWeight: '500', color: '#0f172a' }}>{cfg.title}</p>
              <p style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>{cfg.sub}</p>
            </div>
            <span style={{ fontSize: '12px', fontWeight: '500', color: cfg.color, background: 'white', padding: '4px 12px', borderRadius: '999px' }}>
              {contracts.length} สัญญา
            </span>
          </div>
          {contracts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8', fontSize: '13px' }}>ไม่มีสัญญารอลงนาม</div>
          ) : divisionGroups.map(([divisionName, divisionContracts]) => (
            <div key={divisionName} style={{ borderTop: '1px solid #e2e8f0' }}>
              <div style={{ padding: '11px 16px', background: '#f8fbff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                  <span style={{ width: '28px', height: '28px', borderRadius: '9px', background: '#eaf3ff', color: '#075dcc', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <i className="ti ti-building-community" style={{ fontSize: '16px' }} />
                  </span>
                  <p style={{ fontSize: '13px', fontWeight: '700', color: '#0b3d91', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{divisionName}</p>
                </div>
                <span style={{ fontSize: '11px', fontWeight: '600', color: '#2563c5', background: 'white', border: '1px solid #dbeafe', padding: '3px 10px', borderRadius: '999px', whiteSpace: 'nowrap' }}>
                  {divisionContracts.length} สัญญา
                </span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      {['เลขสัญญา', 'พนักงาน', 'ประเภท', ...(type === 'witness2' ? ['พยานคนที่ 1'] : []), canWitness ? 'จัดการ' : 'สถานะ'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '10px 16px', fontSize: '11px', color: '#94a3b8', fontWeight: '500' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {divisionContracts.map(c => {
                      const isSelf = type === 'witness2' && c.witness1_user_id === currentUserData?.id
                      return (
                        <tr key={c.id} style={{ borderTop: '0.5px solid #f1f5f9' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: '500', color: '#0f172a' }}>{c.contract_no}</td>
                          <td style={{ padding: '12px 16px', fontSize: '13px', color: '#334155' }}>{c.prefix}{c.first_name} {c.last_name}</td>
                          <td style={{ padding: '12px 16px' }}>
                            <span style={{ display: 'inline-flex', padding: '3px 10px', borderRadius: '999px', fontSize: '11px', background: '#eff6ff', color: '#075dcc' }}>{c.emp_type}</span>
                          </td>
                          {type === 'witness2' && (
                            <td style={{ padding: '12px 16px', fontSize: '12px', color: '#64748b' }}>{c.witness1_name || '-'}</td>
                          )}
                          <td style={{ padding: '12px 16px' }}>
                            {canWitness ? (
                              <button onClick={() => openModal(c, type)} disabled={isSelf}
                                style={{ padding: '6px 14px', background: isSelf ? '#f1f5f9' : cfg.btnColor, color: isSelf ? '#94a3b8' : 'white', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: '500', cursor: isSelf ? 'not-allowed' : 'pointer' }}>
                                {isSelf ? 'ท่านเป็นพยาน 1 แล้ว' : cfg.btnLabel}
                              </button>
                            ) : (
                              <span style={{ display: 'inline-flex', padding: '4px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: '500', color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.color}25` }}>
                                {type === 'witness1' ? 'รอพยานคนที่ 1' : 'พยาน 1 ลงนามแล้ว • รอพยานคนที่ 2'}
                              </span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <Layout>
      <ModalComponents />
      <div className="ice-list-page" style={{ maxWidth: '1200px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ fontSize: '20px', fontWeight: '500', color: '#0f172a' }}>{canWitness ? 'ลงนามพยาน' : 'สถานะการลงนามพยาน'}</h1>
          <p style={{ fontSize: '13px', color: '#94a3b8', marginTop: '4px' }}>
            {canWitness ? 'ตรวจสอบและลงนามในฐานะพยาน' : 'ติดตามรายชื่อและสถานะการลงนาม'} • อัปเดต {lastUpdated.toLocaleTimeString('th-TH')}
          </p>
        </div>

        {/* Warning ไม่มีลายเซ็น */}
        {canWitness && !currentUserData?.signature_image && (
          <div style={{ background: '#fef9c3', border: '0.5px solid #fde68a', borderRadius: '12px', padding: '14px 16px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '34px', height: '34px', background: '#fef08a', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <i className="ti ti-alert-triangle" style={{ fontSize: '18px', color: '#f59e0b' }} />
            </div>
            <p style={{ fontSize: '13px', color: '#713f12' }}>
              ท่านยังไม่ได้อัปโหลดลายเซ็น กรุณาไปที่{' '}
              <a href="/users" style={{ color: '#075dcc', fontWeight: '500' }}>จัดการผู้ใช้</a>{' '}
              เพื่ออัปโหลดลายเซ็นก่อน
            </p>
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '80px', color: '#94a3b8', fontSize: '13px' }}>กำลังโหลด...</div>
        ) : (
          <>
            <ContractSection contracts={signedContracts} type="witness1" />
            <ContractSection contracts={witness1Contracts} type="witness2" />
          </>
        )}
      </div>

      {/* Modal */}
      {showModal && contractDetail && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '16px' }}>
          <div style={{ background: 'white', borderRadius: '16px', width: '100%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ padding: '20px 24px', borderBottom: '0.5px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ fontSize: '15px', fontWeight: '500', color: '#0f172a' }}>ตรวจสอบและลงนาม</p>
                <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px' }}>เลขสัญญา {contractDetail.contract_no}</p>
              </div>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '20px' }}>×</button>
            </div>

            <div style={{ padding: '20px 24px' }}>
              {/* ข้อมูลพนักงาน */}
              <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', marginBottom: '14px' }}>
                <p style={{ fontSize: '10px', fontWeight: '500', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '12px' }}>ข้อมูลพนักงาน</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  {[
                    ['ชื่อ-นามสกุล', `${contractDetail.prefix}${contractDetail.first_name} ${contractDetail.last_name}`],
                    ['เลขบัตร', contractDetail.id_card || '-'],
                    ['กองงาน', contractDetail.division_name || '-'],
                    ['ตำแหน่ง', contractDetail.position || '-'],
                    ['วันเริ่ม', new Date(contractDetail.start_date).toLocaleDateString('th-TH')],
                    ['วันหมด', new Date(contractDetail.end_date).toLocaleDateString('th-TH')],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <p style={{ fontSize: '11px', color: '#94a3b8' }}>{label}</p>
                      <p style={{ fontSize: '13px', fontWeight: '500', color: '#0f172a', marginTop: '2px' }}>{value}</p>
                    </div>
                  ))}
                  <div style={{ gridColumn: 'span 2' }}>
                    <p style={{ fontSize: '11px', color: '#94a3b8' }}>ค่าตอบแทนรวม</p>
                    <p style={{ fontSize: '18px', fontWeight: '500', color: '#22c55e', marginTop: '2px' }}>{Number(contractDetail.total_salary).toLocaleString('th-TH')} บาท/เดือน</p>
                  </div>
                </div>
              </div>

              {/* ลายเซ็นพนักงาน */}
              {contractDetail.signatures?.filter(s => s.signer_role === 'employee').map((sig, i) => (
                <div key={i} style={{ background: '#f0fdf4', border: '0.5px solid #bbf7d0', borderRadius: '12px', padding: '14px', marginBottom: '12px' }}>
                  <p style={{ fontSize: '11px', fontWeight: '500', color: '#16a34a', marginBottom: '8px' }}>✅ ลายเซ็นพนักงาน</p>
                  <img src={sig.sig_data} alt="ลายเซ็นพนักงาน" style={{ maxHeight: '56px', border: '0.5px solid #bbf7d0', borderRadius: '8px', background: 'white', padding: '4px' }} />
                  <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '6px' }}>เซ็นเมื่อ {new Date(sig.signed_at).toLocaleString('th-TH')}</p>
                </div>
              ))}

              {/* ลายเซ็นพยาน 1 */}
              {(modalType === 'witness2' || modalType === 'signed1' || modalType === 'signed2') && contractDetail.witness1_sig && (
                <div style={{ background: '#eff6ff', border: '0.5px solid #bfdbfe', borderRadius: '12px', padding: '14px', marginBottom: '12px' }}>
                  <p style={{ fontSize: '11px', fontWeight: '500', color: '#1d4ed8', marginBottom: '4px' }}>✅ ลายเซ็นพยานคนที่ 1</p>
                  <p style={{ fontSize: '11px', color: '#64748b', marginBottom: '8px' }}>{contractDetail.witness1_name}</p>
                  <img src={contractDetail.witness1_sig} alt="ลายเซ็นพยาน 1" style={{ maxHeight: '56px', border: '0.5px solid #bfdbfe', borderRadius: '8px', background: 'white', padding: '4px' }} />
                </div>
              )}

              {/* ลงนามสำเร็จ */}
              {(modalType === 'signed1' || modalType === 'signed2') && (
                <div style={{ background: '#f0fdf4', border: '0.5px solid #bbf7d0', borderRadius: '12px', padding: '14px', marginBottom: '12px' }}>
                  <p style={{ fontSize: '13px', fontWeight: '500', color: '#16a34a', marginBottom: '8px' }}>
                    ✅ {modalType === 'signed1' ? 'ลงนามพยานคนที่ 1 สำเร็จ' : 'ลงนามพยานคนที่ 2 สำเร็จ'}
                  </p>
                  {currentUserData?.signature_image && (
                    <img src={currentUserData.signature_image} alt="ลายเซ็น" style={{ maxHeight: '56px', border: '0.5px solid #bbf7d0', borderRadius: '8px', background: 'white', padding: '4px' }} />
                  )}
                </div>
              )}

              {/* ปุ่ม PDF */}
              {(modalType === 'witness1' || modalType === 'witness2') && (
                <>
                <button onClick={handleViewPDF}
                  style={{ width: '100%', padding: '10px', background: '#1e293b', color: 'white', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '12px' }}>
                  <i className="ti ti-file-text" style={{ fontSize: '16px' }} />
                  ดู PDF สัญญาก่อนลงนาม
                </button>
                <div style={{ marginBottom: '14px' }}>
                  <label htmlFor="approve-confirm-password" style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#334155', marginBottom: '6px' }}>ยืนยันรหัสผ่านของท่านก่อนลงนาม</label>
                  <input id="approve-confirm-password" type="password" value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)} autoComplete="current-password"
                    placeholder="กรอกรหัสผ่านบัญชีปัจจุบัน"
                    style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '10px', fontSize: '13px', outline: 'none' }} />
                  <p style={{ fontSize: '11px', color: '#64748b', marginTop: '5px' }}>ระบบใช้เพื่อยืนยันตัวตนครั้งนี้เท่านั้น และไม่บันทึกรหัสผ่านลง Audit Log</p>
                </div>
                </>
              )}

              {/* ปุ่มลงนาม */}
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => setShowModal(false)}
                  style={{ flex: 1, padding: '10px', background: 'white', color: '#64748b', border: '0.5px solid #e2e8f0', borderRadius: '10px', fontSize: '13px', cursor: 'pointer' }}>
                  {modalType === 'signed1' || modalType === 'signed2' ? 'ปิด' : 'ยกเลิก'}
                </button>
                {modalType !== 'signed1' && modalType !== 'signed2' && (
                  <button onClick={modalType === 'witness1' ? handleWitness1Sign : handleWitness2Sign}
                    disabled={saving || !currentUserData?.signature_image || !confirmPassword}
                    style={{ flex: 1, padding: '10px', background: saving || !currentUserData?.signature_image ? '#a5b4fc' : modalType === 'witness1' ? '#f59e0b' : '#075dcc', color: 'white', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: '500', cursor: saving ? 'not-allowed' : 'pointer' }}>
                    {saving ? 'กำลังบันทึก...' : modalType === 'witness1' ? 'ลงนามพยานคนที่ 1' : 'ลงนามพยานคนที่ 2'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  )
}
